import { useCallback, useEffect, useState } from "react";
import loader from "../../../../../components/recharts/loader.gif";
import { ChevronUp, ChevronDown } from "lucide-react";
import { getBessResultSummary } from "../../../../../Rest_api/RestApi";

interface ScenarioSummaryData {
  scenario_id: number;
  scenario_number: number;
  period: string; // "Scenario 0"
  max_deficit: string | number;
  peak_demand: string | number;
  surplus_deficit_at_pd: string | number;
  percent_re_share_gen: string | number;
  bess_sur_consumed: string | number;
  bess_def_bridged: string | number;
}

interface YearlyData {
  year: number;
  period: string;
  max_deficit: string | number;
  peak_demand: string | number;
  surplus_deficit_at_pd: string | number;
  percent_re_share_gen: string | number;
  bess_sur_consumed: string | number;
  bess_def_bridged: string | number;
  re_gen_cost?: string | number;
  non_re_gen_cost?: string | number;
  cost_before_bess?: string | number;
  cost_after_bess?: string | number;
}

interface MonthlyData {
  year: number;
  month: number;
  month_name: string;
  period: string;
  max_deficit: string | number;
  peak_demand: string | number;
  surplus_deficit_at_pd: string | number;
  percent_re_share_gen: string | number;
  bess_sur_consumed: string | number;
  bess_def_bridged: string | number;
}

const HeaderCell: React.FC<{ title: React.ReactNode; className?: string }> = ({ title, className = "" }) => (
  <th className={`py-2 text-xs px-2 text-center font-medium text-[#F1935C] leading-tight ${className}`}>
    {title}
  </th>
);

const HeaderCell2: React.FC<{ title: React.ReactNode }> = ({ title }) => (
  <th className="py-2 text-xs px-2 text-center font-medium text-[#34656D] !bg-[#F7FAFF] leading-tight">
    {title}
  </th>
);

export default function QuantumSummaryTable({
  refreshKey,
  setError,
  selectedYears,
  setSelectedYears,
  setAvailableYears,
  selectedScenarios,
}: {
  refreshKey: number;
  setError?: any;
  selectedYears: any[];
  setSelectedYears: (years: any[]) => void;
  setAvailableYears: (years: any[]) => void;
  selectedScenarios: Array<{ scenario_id: number, scenario_number: number }>;
}) {
  const [data, setData] = useState<ScenarioSummaryData[]>([]); // Rows to display
  const [scenariosData, setScenariosData] = useState<any>({}); // Full API scenarios object
  const [fullResponse, setFullResponse] = useState<any>(null); // Full API response
  const [loading, setLoading] = useState(false);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);

  // Helper function to parse numeric values
  const parseValue = (value: string | number): number => {
    if (typeof value === "number") return value;
    return parseFloat(value) || 0;
  };

  // Fetch data from API
  const handleFetchData = useCallback(async () => {
    try {
      setLoading(true);
      const scenarioIds = selectedScenarios?.map((s) => s.scenario_id) || [];
      const response = await getBessResultSummary(scenarioIds);



      if (response && response.data_scenariowise) {
        setFullResponse(response);
        setScenariosData(response.scenarios || {});
        setData(response.data_scenariowise);

        // Extract available years from scenarios
        const uniqueYears = new Set<number>();
        if (response.scenarios) {
          Object.values(response.scenarios).forEach((scenario: any) => {
            scenario.data_yearwise?.forEach((item: any) => uniqueYears.add(item.year));
          });
        }

        const yearOptions = Array.from(uniqueYears).sort().map((year) => ({
          value: year,
          label: year.toString(),
        }));
        setAvailableYears(yearOptions);

      } else {
        console.error("Invalid response format:", response);
        setError?.("Invalid data format received from API.");
      }
    } catch (error) {
      console.error("Error fetching data", error);
      setError?.("An error occurred while fetching data.");
    } finally {
      setLoading(false);
    }
  }, [setError, setAvailableYears, selectedScenarios]);

  useEffect(() => {
    handleFetchData();
  }, [handleFetchData, refreshKey]);

  // Handle Filtering based on Selected Years
  useEffect(() => {
    if (!fullResponse) return;

    if (selectedYears && selectedYears.length > 0) {
      const selectedYearValues = selectedYears.map((y: any) => y.value);

      const filteredRows: ScenarioSummaryData[] = [];

      Object.values(fullResponse.scenarios || {}).forEach((scenario: any) => {
        const yearDataList = scenario.data_yearwise?.filter((d: any) => selectedYearValues.includes(d.year)) || [];

        if (yearDataList.length > 0) {
          const match = yearDataList[0];
          filteredRows.push({
            scenario_id: scenario.scenario_id,
            scenario_number: scenario.scenario_number,
            period: `Scenario ${scenario.scenario_number}`,
            max_deficit: match.max_deficit,
            peak_demand: match.peak_demand,
            surplus_deficit_at_pd: match.surplus_deficit_at_pd,
            percent_re_share_gen: match.percent_re_share_gen,
            bess_sur_consumed: match.bess_sur_consumed,
            bess_def_bridged: match.bess_def_bridged
          });
        }
      });

      if (filteredRows.length > 0) {
        setData(filteredRows);
      } else {
        setData([]);
      }

    } else {
      if (fullResponse.data_scenariowise) {
        setData(fullResponse.data_scenariowise);
      }
    }
  }, [selectedYears, fullResponse]);

  const handleRowClick = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const isRowExpanded = (index: number) => expandedRows.includes(index);

  const getYearlyDataForScenario = (scenarioNum: number) => {
    if (!scenariosData) return [];

    const scenarioDataPoint = data.find(d => d.scenario_number === scenarioNum);
    if (!scenarioDataPoint) return [];

    const scenarioId = scenarioDataPoint.scenario_id;
    if (scenarioId === undefined || scenarioId === null) return [];

    const scenario = scenariosData[scenarioId];
    if (!scenario) return [];

    let years = scenario.data_yearwise || [];

    if (selectedYears && selectedYears.length > 0) {
      const selectedYearValues = selectedYears.map((y: any) => y.value);
      years = years.filter((y: any) => selectedYearValues.includes(y.year));
    }

    return years;
  };

  return (
    <div
      className="overflow-auto capitalize h-full max-h-[260px]"
    >
      {loading ? (
        <div className="h-full w-full bg-white flex items-center justify-center">
          <img
            src={loader}
            width={200}
            alt="loader"
            className="block mt-[5rem]"
          />
        </div>
      ) : (
        <>
          {data.length === 0 ? (
            <div className="flex items-center justify-center overflow-auto capitalize rounded-lg w-full h-full">
              <h1 className="chartHeading !text-sm text-center !font-medium w-100">
                No Data Available...
              </h1>
            </div>
          ) : (
            <table id="summary-table" className="w-full table-fixed">
              <thead className="sticky top-0 z-[1] bg-white">
                <tr className="text-sm border-gray-200">
                  <th className="py-2 text-xs px-2 text-left font-medium text-[#F1935C] w-[4%]"></th>
                  <HeaderCell className="w-[12%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        No. of Scenarios
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Max Deficit (MW)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Peak Demand (MW)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        Surplus/Deficit Peak Demand (MW)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        % Gen RE Share
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        BESS Surplus Consumed (MWh)
                      </span>
                    }
                  />
                  <HeaderCell className="w-[14%]"
                    title={
                      <span className="cursor-pointer select-none flex items-center justify-center gap-1">
                        BESS Deficit Bridged (MWh)
                      </span>
                    }
                  />
                </tr>
              </thead>
              <tbody className="text-[#8C8A96] font-normal text-xs">
                {data.map((item, idx) => (
                  <>
                    <tr
                      key={idx}
                      className={`border-b border-gray-50 hover:bg-gray-50 cursor-pointer ${isRowExpanded(idx) ? "border-none" : ""
                        } ${idx % 2 === 0 ? "" : "!bg-[#FBFCFD]"}`}
                      onClick={() => handleRowClick(idx)}
                    >
                      <td className="py-2 px-2 text-center cursor-pointer">
                        {isRowExpanded(idx) ? (
                          <ChevronUp size={20} className="text-[#F1935C]" />
                        ) : (
                          <ChevronDown size={20} className="text-[#F1935C]" />
                        )}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal font-medium ${isRowExpanded(idx)
                          ? "text-[#525062]"
                          : "text-[#F1935C]"
                          }`}
                      >
                        {item.scenario_number}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.max_deficit).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.peak_demand).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.surplus_deficit_at_pd).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.percent_re_share_gen).toFixed(2)}%
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.bess_sur_consumed).toFixed(2)}
                      </td>
                      <td
                        className={`py-2 px-2 text-center whitespace-normal ${isRowExpanded(idx) ? "text-[#525062] font-medium" : ""
                          }`}
                      >
                        {parseValue(item.bess_def_bridged).toFixed(2)}
                      </td>
                    </tr>

                    {/* Expanded Yearly Data - Accordion Style */}
                    {isRowExpanded(idx) && (
                      <tr>
                        <td colSpan={8} className="bg-[#F7FAFF] p-2">
                          <div
                            className="capitalize text-nowrap !border !border-[#F1935C] rounded-xl overflow-hidden"
                          >
                            <table className="w-full table-fixed">
                              <tbody className="text-[#8C8A96] font-normal text-xs">
                                {getYearlyDataForScenario(item.scenario_number)
                                  .length > 0 ? (
                                  getYearlyDataForScenario(item.scenario_number).map(
                                    (yearItem: YearlyData, yearIdx: number) => (
                                      <tr
                                        key={yearIdx}
                                        className="text-center [&>td]:border-none bg-[#F7FAFF]"
                                      >
                                        <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#34656D] w-[4%]"></td>
                                        <td className="py-2 px-2 text-center whitespace-normal font-medium text-[#34656D] w-[12%]">
                                          {yearItem.year}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            yearItem.max_deficit
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            yearItem.peak_demand
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            yearItem.surplus_deficit_at_pd
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            yearItem.percent_re_share_gen
                                          ).toFixed(2)}
                                          %
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            yearItem.bess_sur_consumed
                                          ).toFixed(2)}
                                        </td>
                                        <td className="py-2 px-2 text-center whitespace-normal w-[14%]">
                                          {parseValue(
                                            yearItem.bess_def_bridged
                                          ).toFixed(2)}
                                        </td>
                                      </tr>
                                    )
                                  )
                                ) : (
                                  <tr>
                                    <td colSpan={7} className="text-center py-8">
                                      <h1 className="chartHeading !text-sm text-center !font-medium w-full">
                                        No Yearly Data Available...
                                      </h1>
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  );
}